import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useUser } from '../context/UserContext';
import { getProfile, updateUserProfile, changePassword } from '../api';
import axios from 'axios';
import API_URL from '../config';
import ErrorBoundary from './ErrorBoundary';
import Modal from './Modal';
import Onboarding from './Onboarding';
import ProfileDebugPanel from './ProfileDebugPanel';
import { Edit, Save, RefreshCw, Lock, Target, Info, AlertCircle, CheckCircle, X, User, Trophy, Brain, Sparkles, Info as InfoCircle } from 'lucide-react';
import logger from '../utils/logger';
import './DesktopComponentStyles.css';

// Sports and positions data
const SPORTS_POSITIONS_DATA = {
  'Badminton': ['Singles', 'Doubles', 'Mixed Doubles'],
  'Baseball': ['Pitcher', 'Catcher', 'First Base', 'Second Base', 'Third Base', 'Shortstop', 'Left Field', 'Center Field', 'Right Field', 'Designated Hitter', 'Utility Player'],
  'Basketball': ['Point Guard', 'Shooting Guard', 'Small Forward', 'Power Forward', 'Center'],
  'Bowling': ['Individual Bowler', 'Team Bowler'],
  'Cheerleading': ['Flyer', 'Base', 'Back Spot', 'Tumbler', 'Stunter'],
  'Cross Country': ['Runner'],
  'Dance': ['Pom', 'Jazz', 'Hip-Hop', 'Kick', 'Contemporary'],
  'Field Hockey': ['Goalkeeper', 'Defender', 'Midfielder', 'Forward'],
  'Flag Football': ['Quarterback', 'Running Back', 'Wide Receiver', 'Center', 'Linebacker', 'Cornerback', 'Safety', 'Rusher'],
  'Football': ['Quarterback', 'Running Back', 'Fullback', 'Wide Receiver', 'Tight End', 'Offensive Tackle', 'Offensive Guard', 'Center', 'Defensive End', 'Defensive Tackle', 'Linebacker', 'Cornerback', 'Safety', 'Kicker', 'Punter', 'Long Snapper', 'Kick Returner', 'Punt Returner'],
  'Golf': ['Individual Golfer', 'Team Golfer'],
  'Gymnastics': ['Vault', 'Uneven Bars', 'Balance Beam', 'Floor Exercise', 'All-Around', 'Pommel Horse', 'Still Rings', 'Parallel Bars', 'Horizontal Bar'],
  'Ice Hockey': ['Center', 'Left Wing', 'Right Wing', 'Defenseman', 'Goalie'],
  'Lacrosse': ['Attack', 'Midfield', 'Defense', 'Goalie', 'Faceoff Specialist', 'Long Stick Midfielder (LSM)'],
  'Rowing': ['Sculler', 'Sweep Rower', 'Coxswain', 'Bow Seat', 'Stroke Seat'],
  'Rugby': ['Prop', 'Hooker', 'Lock', 'Flanker', 'Number Eight', 'Scrum-half', 'Fly-half', 'Center', 'Wing', 'Fullback'],
  'Ski': ['Alpine', 'Freestyle', 'Cross-Country', 'Ski Jumping', 'Nordic Combined', 'Snowboard'],
  'Soccer': ['Goalkeeper', 'Right Back', 'Left Back', 'Center Back', 'Defensive Midfielder', 'Central Midfielder', 'Attacking Midfielder', 'Right Wing', 'Left Wing', 'Striker', 'Forward'],
  'Softball': ['Pitcher', 'Catcher', 'First Base', 'Second Base', 'Third Base', 'Shortstop', 'Left Field', 'Center Field', 'Right Field', 'Designated Player', 'Flex Player', 'Utility Player'],
  'Swimming': ['Freestyle', 'Backstroke', 'Breaststroke', 'Butterfly', 'Individual Medley', 'Freestyle Relay', 'Medley Relay', 'Distance Freestyle', 'Sprint Freestyle'],
  'Tennis': ['Singles', 'Doubles', 'Mixed Doubles'],
  'Track and Field': ['Sprinter', 'Middle Distance', 'Long Distance', 'Hurdler', 'High Jump', 'Long Jump', 'Triple Jump', 'Pole Vault', 'Shot Put', 'Discus', 'Javelin', 'Hammer Throw', 'Decathlon/Heptathlon'],
  'Volleyball': ['Outside Hitter', 'Opposite Hitter', 'Setter', 'Middle Blocker', 'Libero', 'Defensive Specialist'],
  'Water Polo': ['Goalkeeper', 'Center Forward', 'Center Back', 'Driver', 'Winger', 'Utility Player'],
  'Wrestling': ['Lightweight', 'Welterweight', 'Middleweight', 'Heavyweight', 'Greco-Roman', 'Freestyle', 'Folkstyle']
};

// Get sorted list of sports
const SPORTS_LIST = Object.keys(SPORTS_POSITIONS_DATA).sort();
SPORTS_LIST.push('Other');

// Actual Profile component
const ProfileContent = () => {
  // DEBUG: Log useUser() context to diagnose ReferenceError
  const userContext = useUser();
  console.log('DEBUG ProfileContent userContext:', userContext);
  if (!userContext) {
    throw new Error('useUser() returned undefined - possible circular import or context issue');
  }
  const { 
    user, 
    isAuthenticated, 
    fetchUserProfile: contextFetchProfile, 
    updateProfile: contextUpdateProfile 
  } = userContext;

  // (all hooks and state initializations remain above)


  const userId = user?.userId || localStorage.getItem('userId');
  const token = localStorage.getItem('token');
  
  // User data state
  const [userData, setUserData] = useState({
    name: '',
    email: '',
    birthYear: '',
    gender: '',
    role: '',
    sport: '',
    sportPosition: '',
    yearsPlaying: 0,
    whyIPlaySport: '',
    journey: '',
    goals: '',
    expectations: '', 
    currentMentalState: '',
    desiredMentalState: ''
  });
  
  // Team data state
  const [teamDetails, setTeamDetails] = useState(null);
  const [isLoadingTeam, setIsLoadingTeam] = useState(false);
  const [teamError, setTeamError] = useState(null);
  // Add a key to force re-render when team details change
  const [teamUpdateKey, setTeamUpdateKey] = useState(0);
  
  // UI state
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [message, setMessage] = useState(null);
  const [fetchAttempted, setFetchAttempted] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(new Date());
  const [localDataUsed, setLocalDataUsed] = useState(false);

  // Ref to track if the effect has already run
  const effectRan = useRef(false);

  // Editing states
  const [editingPersonal, setEditingPersonal] = useState(false);
  const [editingSport, setEditingSport] = useState(false);
  const [editData, setEditData] = useState({});
  
  // Modal states
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [showOnboardingModal, setShowOnboardingModal] = useState(false);
  
  // Password change states
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [passwordError, setPasswordError] = useState(null);
  const [passwordMessage, setPasswordMessage] = useState(null);
  
  // Sport-related states
  const [positionOptions, setPositionOptions] = useState([]);

  // Helper function to process and validate user data
  const processUserData = useCallback((data) => {
    logger.debug('Processing user data:', data);
    
    // Default empty user data structure
    const processedData = {
      name: '',
      email: '',
      birthYear: '',
      gender: '',
      role: '',
      sport: '',
      sportPosition: '',
      yearsPlaying: '',
      whyIPlaySport: '',
      journey: '',
      goals: '',
      expectations: '',
      currentMentalState: '',
      desiredMentalState: '',
      userId: '',
      id: '',
      age: ''
    };
    
    // Ensure userId is consistent
    const userId = localStorage.getItem('userId');
    if (userId) {
      processedData.userId = userId;
      processedData.id = userId;
    }
    
    // If we have data, override the defaults
    if (data) {
      // Copy direct properties
      Object.keys(processedData).forEach(key => {
        if (data[key] !== undefined && data[key] !== null) {
          processedData[key] = data[key];
          logger.debug(`Setting ${key} from direct data:`, data[key]);
        }
      });
      
      // Handle nested properties - prioritize onboardingResponses from API
      if (data.onboardingResponses) {
        logger.debug('Found onboardingResponses in API data:', data.onboardingResponses);
        
        // Process each onboarding field
        Object.entries(data.onboardingResponses).forEach(([key, value]) => {
          // Only update if the value is not empty and the field exists in our processedData object
          if (value !== undefined && value !== null && value !== '' && processedData.hasOwnProperty(key)) {
            processedData[key] = value;
            logger.debug(`Setting ${key} from onboardingResponses:`, value);
          }
        });
      }
      
      // Ensure userId is set from data if available
      if (data.userId || data._id) {
        processedData.userId = data.userId || data._id;
        processedData.id = data.userId || data._id;
      }
    } else {
      // If no API data provided, try to load from localStorage
      try {
        // First try to load the user profile from localStorage
        const userProfileStr = localStorage.getItem('user');
        if (userProfileStr) {
          const userProfile = JSON.parse(userProfileStr);
          logger.debug('Loaded user profile from localStorage:', userProfile);
          
          // Copy basic profile fields
          Object.keys(processedData).forEach(key => {
            if (userProfile[key] && userProfile[key] !== '') {
              processedData[key] = userProfile[key];
              logger.debug(`Setting ${key} from localStorage user:`, userProfile[key]);
            }
          });
          
          // Copy onboarding fields if available in the user profile
          if (userProfile.onboardingResponses) {
            Object.entries(userProfile.onboardingResponses).forEach(([key, value]) => {
              if (value !== undefined && value !== null && value !== '' && processedData.hasOwnProperty(key)) {
                processedData[key] = value;
                logger.debug(`Setting ${key} from localStorage onboardingResponses:`, value);
              }
            });
          }
          
          // Ensure userId is set from userProfile if available
          if (userProfile.userId || userProfile._id) {
            processedData.userId = userProfile.userId || userProfile._id;
            processedData.id = userProfile.userId || userProfile._id;
          }
        }
      } catch (error) {
        logger.error('Error parsing user profile from localStorage:', error);
      }
    }
    
    // Try to parse onboarding responses from localStorage if needed
    try {
      const onboardingResponsesStr = localStorage.getItem('onboardingResponses');
      if (onboardingResponsesStr) {
        const parsedResponses = JSON.parse(onboardingResponsesStr);
        logger.debug('Found onboarding responses in localStorage:', parsedResponses);
        
        // Fill in missing fields from localStorage if available
        Object.entries(parsedResponses).forEach(([key, value]) => {
          if ((!processedData[key] || processedData[key] === '') && value && value !== '' && processedData.hasOwnProperty(key)) {
            processedData[key] = value;
            logger.debug(`Setting ${key} from localStorage onboardingResponses:`, value);
          }
        });
      }
    } catch (error) {
      logger.error('Error parsing onboarding responses from localStorage:', error);
    }
    
    logger.debug('Final processed user data:', processedData);
    return processedData;
  }, []);

  // Function to fetch team details for the current user
  const fetchTeamDetails = useCallback(async () => {
    console.log('fetchTeamDetails function called');
    if (isLoadingTeam) {
      logger.debug('Team details fetch already in progress, skipping duplicate call');
      return;
    }
    
    // Debug current state
    if (process.env.NODE_ENV === 'development') {
      logger.debug('Current teamDetails state before fetch:', teamDetails ? 'Has data' : 'null');
    }

    const token = localStorage.getItem('token');
    const userId = localStorage.getItem('userId');
    
    if (!token || !userId) {
      logger.warn('Cannot fetch team details: missing token or userId');
      return;
    }

    setIsLoadingTeam(true);
    setTeamError(null);

    try {
      const url = `${API_URL}/api/users/me/team-details`;
      logger.debug('Fetching team details from:', url);
      logger.debug('Using userId:', userId);

      const response = await axios.get(url, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      logger.debug('Team details API response received');
      
      // Add more detailed logging of the response data
      if (response.data) {
        logger.debug('Team details raw data received');
        logger.debug('Response data structure:', response.data);
      }

      // The API returns team data directly in response.data, not nested under teamDetails
      if (response.data && (response.data.teamId || response.data.members)) {
        if (process.env.NODE_ENV === 'development') {
          logger.debug('Team details fetched successfully');
          logger.debug('Team details structure:', {
            hasTeamId: !!response.data.teamId,
            hasMembers: !!response.data.members,
            membersCount: response.data.members?.length || 0,
            teamId: response.data.teamId
          });
        }
        
        // Force a deep copy of the team details to ensure state update
        const teamDetailsData = JSON.parse(JSON.stringify(response.data));
        logger.debug('Setting teamDetails state with:', teamDetailsData);
        
        // IMPORTANT: Force React to recognize state change by creating a new object
        setTeamDetails({...teamDetailsData});
        
        if (process.env.NODE_ENV === 'development') {
          console.log('Team details updated with:', teamDetailsData);
        }
        
        // Increment the key to force a re-render
        setTeamUpdateKey(prev => prev + 1);
        
        // Store in session storage as backup
        try {
          sessionStorage.setItem('teamDetails', JSON.stringify(teamDetailsData));
          logger.debug('Team details stored in session storage');
        } catch (e) {
          logger.error('Error storing team details in session storage:', e);
        }
      } else {
        logger.debug('No team details found in response');
        setTeamDetails(null);
      }
    } catch (error) {
      logger.error('Error fetching team details:', error);
      logger.debug('Team details fetch error details:', {
        message: error.message,
        status: error.response?.status,
        statusText: error.response?.statusText
      });
      setTeamError('Failed to load team information. Please try again later.');
      
      // Try to load from session storage as fallback
      try {
        const storedTeamDetails = sessionStorage.getItem('teamDetails');
        if (storedTeamDetails) {
          const parsedTeamDetails = JSON.parse(storedTeamDetails);
          logger.debug('Loading team details from session storage:', parsedTeamDetails);
          setTeamDetails({...parsedTeamDetails});
          // Increment the key to force a re-render
          setTeamUpdateKey(prev => prev + 1);
        }
      } catch (e) {
        logger.error('Error loading team details from session storage:', e);
      }
    } finally {
      setIsLoadingTeam(false);
    }
  }, [isLoadingTeam, API_URL]);

  // Function to fetch user profile data from the database
  const fetchUserProfile = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    setFetchAttempted(true);
    
    logger.debug('Fetching user profile data...');
    
    try {
      let response;
      
      if (contextFetchProfile) {
        logger.debug('Using context fetch profile function');
        response = await contextFetchProfile();
      } else {
        logger.debug('Using API getProfile function');
        response = await getProfile();
      }
      
      if (response && response.data) {
        // Check if we got placeholder data
        if (response.data.placeholder === true) {
          logger.warn('Received placeholder data from API');
          throw new Error('Placeholder data received');
        }
        
        // Log the full data structure to help debug
        logger.debug('Full user data structure:', JSON.stringify(response.data));
        
        // Check if onboardingResponses exist in the response
        if (response.data.onboardingResponses) {
          logger.debug('Onboarding responses found in API response:', response.data.onboardingResponses);
        } else {
          logger.warn('No onboardingResponses found in API response');
        }
        
        const processedData = processUserData(response.data);
        
        // Debug the processed data to see if onboarding responses are included
        logger.debug('Processed user data:', {
          hasWhyIPlaySport: !!processedData.whyIPlaySport,
          whyIPlaySport: processedData.whyIPlaySport,
          hasJourney: !!processedData.journey,
          journey: processedData.journey,
          hasGoals: !!processedData.goals,
          goals: processedData.goals,
          hasExpectations: !!processedData.expectations,
          expectations: processedData.expectations,
          hasCurrentMentalState: !!processedData.currentMentalState,
          currentMentalState: processedData.currentMentalState,
          hasDesiredMentalState: !!processedData.desiredMentalState,
          desiredMentalState: processedData.desiredMentalState
        });
        
        // Store the raw data in localStorage for debugging
        try {
          localStorage.setItem('lastProfileResponse', JSON.stringify(response.data));
        } catch (e) {
          logger.error('Error storing raw profile data in localStorage:', e);
        }
        
        setUserData(processedData);
        setLastUpdated(new Date());
        setLocalDataUsed(false);
        
        // Return the processed data so it can be stored in session storage
        return processedData;
      } else {
        logger.warn('Profile data response was empty or invalid');
        // Log more details about the empty response
        logger.debug('Empty response details:', {
          responseExists: !!response,
          responseDataExists: !!(response && response.data),
          responseType: response ? typeof response : 'undefined',
          dataType: response && response.data ? typeof response.data : 'undefined',
          timestamp: new Date().toISOString()
        });
        throw new Error('Empty or invalid response');
      }
    } catch (error) {
      logger.error('Error fetching profile:', error);
      // Log the full error details
      logger.debug('Profile fetch error details:', {
        message: error.message,
        stack: error.stack,
        timestamp: new Date().toISOString()
      });
      
      setError('Failed to load profile data. Please try again later.');
      setLocalDataUsed(true);

      // Use a more robust fallback approach
      logger.debug('Attempting to load fallback data from localStorage');
      const fallbackData = processUserData(null);
      
      if (Object.values(fallbackData).some(value => value)) {
        logger.debug('Using fallback data from localStorage:', fallbackData);
        setUserData(prevData => ({
          ...prevData,
          ...fallbackData
        }));
        
        // Return the fallback data so it can be stored in session storage
        return fallbackData;
      } else {
        logger.warn('Fallback data is also empty, user profile cannot be loaded');
        // Create a minimal placeholder user to prevent UI issues
        const userId = localStorage.getItem('userId');
        const placeholderData = {
          name: 'User',
          email: '',
          userId: userId || 'unknown',
          id: userId || 'unknown',
          isLocalFallback: true
        };
        setUserData(placeholderData);
        // Consider showing a more specific error to the user
        setError('Unable to load your profile. Please try logging out and back in.');
        
        // Return the placeholder data so it can be stored in session storage
        return placeholderData;
      }
    } finally {
      setIsLoading(false);
      logger.debug('fetchUserProfile completed, isLoading set to false');
    }
  }, [contextFetchProfile, processUserData]);

  // Calculate age from birth year
  const calculateAge = (birthYear) => {
    if (!birthYear) return null;
    const currentYear = new Date().getFullYear();
    return currentYear - parseInt(birthYear);
  };

  // Handle input change for profile editing
  const handleEditChange = (e) => {
    const { name, value } = e.target;
    setEditData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Handle sport change to update position options
  const handleSportChange = (e) => {
    const sportValue = e.target.value;
    setEditData(prev => ({
      ...prev,
      sport: sportValue,
      sportPosition: '' // Reset position when sport changes
    }));

    if (SPORTS_POSITIONS_DATA[sportValue]) {
      setPositionOptions(SPORTS_POSITIONS_DATA[sportValue]);
    } else {
      setPositionOptions([]);
    }
  };

  // Handle position change
  const handlePositionChange = (e) => {
    const positionValue = e.target.value;
    setEditData(prev => ({
      ...prev,
      sportPosition: positionValue
    }));
  };

  // Handle input change for password change
  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    setPasswordData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Start editing personal information
  const startEditingPersonal = () => {
    setEditingPersonal(true);
    setEditData({
      ...userData,
      name: userData.name || '',
      email: userData.email || '',
      birthYear: userData.birthYear || '',
      gender: userData.gender || ''
    });
  };

  // Start editing sport information
  const startEditingSport = () => {
    setEditingSport(true);
    setEditData({
      ...userData,
      sport: userData.sport || '',
      sportPosition: userData.sportPosition || '',
      yearsPlaying: userData.yearsPlaying || 0
    });

    if (userData.sport && SPORTS_POSITIONS_DATA[userData.sport]) {
      setPositionOptions(SPORTS_POSITIONS_DATA[userData.sport]);
    }
  };

  // Cancel editing
  const cancelEditingPersonal = () => {
    setEditingPersonal(false);
  };

  const cancelEditingSport = () => {
    setEditingSport(false);
  };

  // Save profile changes
  const savePersonalChanges = async () => {
    try {
      setIsLoading(true);
      setError(null);

      if (!editData.name || !editData.email) {
        setError('Name and email are required');
        setIsLoading(false);
        return;
      }

      const updateData = {
        name: editData.name,
        email: editData.email,
        birthYear: editData.birthYear,
        gender: editData.gender
      };

      await updateUserProfile(updateData);

      setUserData(prev => ({
        ...prev,
        ...updateData
      }));

      setMessage('Personal information updated successfully');
      setEditingPersonal(false);
      setLastUpdated(new Date());

      setTimeout(() => {
        setMessage(null);
      }, 3000);
    } catch (error) {
      logger.error('Error saving personal changes:', error);
      setError(error.message || 'Failed to update profile');
    } finally {
      setIsLoading(false);
    }
  };

  const saveSportChanges = async () => {
    try {
      setIsLoading(true);
      setError(null);

      const updateData = {
        sport: editData.sport,
        sportPosition: editData.sportPosition,
        yearsPlaying: editData.yearsPlaying
      };

      await updateUserProfile(updateData);

      setUserData(prev => ({
        ...prev,
        ...updateData
      }));

      setMessage('Sport information updated successfully');
      setEditingSport(false);
      setLastUpdated(new Date());

      setTimeout(() => {
        setMessage(null);
      }, 3000);
    } catch (error) {
      logger.error('Error saving sport changes:', error);
      setError(error.message || 'Failed to update profile');
    } finally {
      setIsLoading(false);
    }
  };

  // Handle password change submission
  const handlePasswordSubmit = async (e) => {
    e.preventDefault();

    try {
      setIsLoading(true);
      setPasswordError(null);

      if (!passwordData.currentPassword || !passwordData.newPassword || !passwordData.confirmPassword) {
        setPasswordError('All fields are required');
        setIsLoading(false);
        return;
      }

      if (passwordData.newPassword !== passwordData.confirmPassword) {
        setPasswordError('New passwords do not match');
        setIsLoading(false);
        return;
      }

      if (passwordData.newPassword.length < 8) {
        setPasswordError('New password must be at least 8 characters');
        setIsLoading(false);
        return;
      }

      await changePassword({
        currentPassword: passwordData.currentPassword,
        newPassword: passwordData.newPassword
      });

      setPasswordData({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      });

      setPasswordMessage('Password changed successfully');

      setTimeout(() => {
        setPasswordMessage(null);
        setShowPasswordModal(false);
      }, 2000);
    } catch (error) {
      logger.error('Error changing password:', error);
      setPasswordError(error.message || 'Failed to change password');
    } finally {
      setIsLoading(false);
    }
  };

  // Handle onboarding completion
  const handleOnboardingComplete = (onboardingData) => {
    logger.debug('Onboarding completed with data:', onboardingData);

    const whyIPlayValue = onboardingData.whyIPlaySport || 
                          onboardingData.why_i_play || 
                          onboardingData.whyIPlay || 
                          "Complete onboarding to share why you play your sport";

    try {
      localStorage.setItem('onboardingResponses', JSON.stringify({
        whyIPlaySport: whyIPlayValue,
        why_i_play: whyIPlayValue,
        whyIPlay: whyIPlayValue,
        journey: onboardingData.journey || '',
        goals: onboardingData.goals || '',
        expectations: onboardingData.expectations || '',
        currentMentalState: onboardingData.currentMentalState || '',
        desiredMentalState: onboardingData.desiredMentalState || ''
      }));

      localStorage.setItem('responses', JSON.stringify({
        whyIPlaySport: whyIPlayValue,
        why_i_play: whyIPlayValue,
        whyIPlay: whyIPlayValue,
        journey: onboardingData.journey || '',
        goals: onboardingData.goals || '',
        expectations: onboardingData.expectations || '',
        currentMentalState: onboardingData.currentMentalState || '',
        desiredMentalState: onboardingData.desiredMentalState || ''
      }));

      logger.debug('Saved onboarding data to localStorage with multiple field formats');
    } catch (error) {
      logger.error('Error saving onboarding data to localStorage:', error);
    }

    // Update local state immediately for better UX
    setUserData(prev => ({
      ...prev,
      whyIPlaySport: whyIPlayValue,
      why_i_play: whyIPlayValue,
      whyIPlay: whyIPlayValue,
      journey: onboardingData.journey || prev.journey,
      goals: onboardingData.goals || prev.goals,
      expectations: onboardingData.expectations || prev.expectations,
      currentMentalState: onboardingData.currentMentalState || prev.currentMentalState,
      desiredMentalState: onboardingData.desiredMentalState || prev.desiredMentalState
    }));

    // Create the onboarding responses object in the format expected by the API
    const onboardingResponses = {
      whyIPlaySport: whyIPlayValue,
      why_i_play: whyIPlayValue,
      whyIPlay: whyIPlayValue,
      journey: onboardingData.journey || '',
      goals: onboardingData.goals || '',
      expectations: onboardingData.expectations || '',
      currentMentalState: onboardingData.currentMentalState || '',
      desiredMentalState: onboardingData.desiredMentalState || ''
    };

    try {
      // Send the update to the server with the properly structured onboardingResponses
      updateUserProfile({
        onboardingResponses: onboardingResponses
      });
      logger.debug('Updated profile on server with onboardingResponses:', onboardingResponses);
    } catch (error) {
      logger.error('Error updating profile with onboarding data:', error);
    }

    // Fetch the updated profile after a short delay to ensure server has processed the update
    setTimeout(() => {
      fetchUserProfile();
    }, 500);

    setShowOnboardingModal(false);
    setMessage('Mental performance profile updated successfully');

    setTimeout(() => {
      setMessage(null);
    }, 3000);
  };

  // Monitor teamDetails changes (only in development)
  useEffect(() => {
    if (process.env.NODE_ENV === 'development') {
      console.log('Team details updated, key:', teamUpdateKey);
    }
  }, [teamDetails, teamUpdateKey]);

  // Dedicated useEffect for fetching team details
  useEffect(() => {
    const fetchTeamInfo = async () => {
      if (process.env.NODE_ENV === 'development') {
        logger.debug('Fetching team details (mount only)');
      }
      await fetchTeamDetails();
    };
    
    fetchTeamInfo();
    // Empty dependency array means this only runs once on mount
  }, []);

  // Add useEffect to trigger user profile data fetching when component mounts
  useEffect(() => {
    const componentId = Math.random().toString(36).substring(2, 9);
    logger.debug(`Profile component mounted with ID: ${componentId}`);
    logger.debug('Auth status:', isAuthenticated);
    logger.debug('User ID from storage:', localStorage.getItem('userId'));
    logger.debug('Token available:', !!localStorage.getItem('token'));
    logger.debug('Current userData state:', userData);

    // Use sessionStorage to track if we've already fetched data in this session
    // Add a timestamp to detect stale session data (older than 30 minutes)
    const sessionDataStr = sessionStorage.getItem('profileDataFetched');
    let hasFetchedData = false;
    let fetchTimestamp = 0;
    let sessionUserData = null;
    
    if (sessionDataStr) {
      try {
        const sessionData = JSON.parse(sessionDataStr);
        fetchTimestamp = sessionData.timestamp || 0;
        sessionUserData = sessionData.userData || null;
        const currentTime = Date.now();
        const thirtyMinutesMs = 30 * 60 * 1000;
        
        // Consider data fresh if fetched less than 30 minutes ago AND we have actual user data
        hasFetchedData = (currentTime - fetchTimestamp) < thirtyMinutesMs && sessionUserData && sessionUserData.name && sessionUserData.email;
      } catch (e) {
        logger.error('Error parsing session data:', e);
        hasFetchedData = false;
      }
    }

    // Team details are now fetched in a separate useEffect
    
    if (!hasFetchedData) {
      logger.debug(`Running fetch operations for component ${componentId}`);
      let timer = setTimeout(() => {
        fetchUserProfile().then(profileData => {
          if (profileData) {
            sessionStorage.setItem('profileDataFetched', JSON.stringify({
              fetched: true,
              timestamp: Date.now(),
              userData: profileData
            }));
            logger.debug('User data stored in session storage');
          }
        });
      }, 100);
    } else {
      logger.debug(`Skipping fetch operations - already fetched in this session (component ID: ${componentId})`);
      logger.debug('Loading user data from session storage');
      if (sessionUserData) {
        setUserData(sessionUserData);
        const storedTeamDetails = sessionStorage.getItem('teamDetails');
        if (storedTeamDetails) {
          try {
            const parsedTeamDetails = JSON.parse(storedTeamDetails);
            setTeamDetails({...parsedTeamDetails});
            logger.debug('Team details loaded from session storage:', parsedTeamDetails);
          } catch (e) {
            logger.error('Error parsing team details from session storage:', e);
          }
        }
        setIsLoading(false);
        setFetchAttempted(true);
        logger.debug('User data loaded from session storage:', sessionUserData);
      } else {
        logger.debug('No user data in session storage despite having fetched flag, forcing fetch');
        let timer = setTimeout(() => {
          fetchUserProfile();
          fetchTeamDetails();
        }, 100);
      }
    }
    return () => {
      // No need to clear timer as it's scoped to its own block
      logger.debug(`Profile component with ID ${componentId} unmounted`);
    };
  }, [fetchUserProfile, fetchTeamDetails, isAuthenticated]);

  // Show loading state
  if (isLoading && !fetchAttempted) {
    logger.debug('Rendering loading state, isLoading:', isLoading, 'fetchAttempted:', fetchAttempted);
    return (
      <div className="flex items-center justify-center min-h-[50vh]">
        <div className="flex flex-col items-center gap-4">
          <span className="loading loading-spinner loading-lg text-primary"></span>
          <p className="text-sm text-base-content/60">Loading your profile...</p>
        </div>
      </div>
    );
  }

  // Show error state
  if (error && !userData.name) {
    return (
      <div className="container mx-auto px-4 py-8 max-w-5xl">
        <div className="flex items-center justify-center min-h-[50vh]">
          <div className="card bg-base-100 shadow-lg max-w-md w-full desktop-standard-card">
            <div className="card-body text-center">
              <div className="flex justify-center mb-4">
                <AlertCircle className="w-12 h-12 text-error" />
              </div>
              <h2 className="text-xl font-bold text-error mb-2">Error Loading Profile</h2>
              <p className="text-base-content/70 mb-4">{error}</p>
              <button 
                onClick={fetchUserProfile} 
                className="btn btn-primary"
                disabled={isLoading}
              >
                {isLoading ? (
                  <>
                    <span className="loading loading-spinner loading-sm"></span>
                    Retrying...
                  </>
                ) : (
                  <>
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Try Again
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Show profile data
  return (
    <div className="desktop-standard-container max-w-5xl pt-4" style={{ width: '1024px !important', maxWidth: '1024px !important', margin: '0 auto !important', boxSizing: 'border-box !important' }}>
      {/* Profile Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">My Profile</h1>
          <p className="text-gray-600 mt-1">
            Manage your personal information and preferences
          </p>
        </div>
        
        
      </div>
      
      {error && (
        <div className="alert alert-error shadow-sm mb-6">
          <AlertCircle className="w-5 h-5 flex-shrink-0" />
          <span>{error}</span>
        </div>
      )}
      
      {message && (
        <div className="alert alert-success shadow-sm mb-6">
          <CheckCircle className="w-5 h-5 flex-shrink-0" />
          <span>{message}</span>
        </div>
      )}
      
      {localDataUsed && (
        <div className="alert alert-warning shadow-sm mb-6">
          <Info className="w-5 h-5 flex-shrink-0" />
          <span>
            We couldn't connect to the server, so we're showing locally stored data. 
            Some information might be outdated.
          </span>
        </div>
      )}
      
      <div className="grid grid-cols-1 gap-6">
        {/* Left Column - Personal Info */}
        <div className="lg:col-span-2 space-y-6">
          {/* Personal Information Card */}
          <div className="card bg-base-100 shadow-md hover:shadow-lg transition-shadow duration-200 desktop-standard-card">
            <div className="card-body p-3 sm:p-4">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-xl font-bold text-primary whitespace-nowrap">Personal Information</h3>
                <button
                  onClick={editingPersonal ? cancelEditingPersonal : startEditingPersonal}
                  className="btn btn-ghost btn-sm btn-square hover:bg-base-200 transition-colors duration-200"
                  aria-label={editingPersonal ? 'Cancel editing' : 'Edit personal information'}
                >
                  {editingPersonal ? 'Cancel' : 'Edit'}
                </button>
              </div>
              
              <div className="min-h-[3rem]">
                {editingPersonal ? (
                  <div className="space-y-4">
                    <div className="form-control">
                      <label className="label">
                        <span className="label-text font-medium">Full Name</span>
                      </label>
                      <input
                        type="text"
                        name="name"
                        value={editData.name || ''}
                        onChange={handleEditChange}
                        className="input input-bordered w-full focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all duration-200"
                        placeholder="Your full name"
                      />
                    </div>
                    
                    <div className="form-control">
                      <label className="label">
                        <span className="label-text font-medium">Email</span>
                      </label>
                      <input
                        type="email"
                        name="email"
                        value={editData.email || ''}
                        onChange={handleEditChange}
                        className="input input-bordered w-full focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all duration-200"
                        placeholder="Your email address"
                      />
                    </div>
                    
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div className="form-control">
                        <label className="label">
                          <span className="label-text font-medium">Birth Year</span>
                        </label>
                        <input
                          type="number"
                          name="birthYear"
                          value={editData.birthYear || ''}
                          onChange={handleEditChange}
                          className="input input-bordered w-full focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all duration-200"
                          placeholder="e.g., 1995"
                          min="1900"
                          max={new Date().getFullYear()}
                        />
                      </div>
                      
                      <div className="form-control">
                        <label className="label">
                          <span className="label-text font-medium">Gender</span>
                        </label>
                        <select
                          name="gender"
                          value={editData.gender || ''}
                          onChange={handleEditChange}
                          className="select select-bordered w-full focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all duration-200"
                        >
                          <option value="">Select gender</option>
                          <option value="male">Male</option>
                          <option value="female">Female</option>
                          <option value="non_binary">Non-binary</option>
                          <option value="prefer_not_to_say">Prefer not to say</option>
                        </select>
                      </div>
                    </div>
                    
                    <div className="divider my-4"></div>
                    
                    <div className="flex justify-between items-center">
                      <button 
                        onClick={() => setShowPasswordModal(true)} 
                        className="btn btn-primary btn-sm"
                      >
                        Change Password
                      </button>
                      
                      <div className="flex gap-2">
                        <button 
                          onClick={cancelEditingPersonal} 
                          className="btn btn-ghost btn-sm"
                          disabled={isLoading}
                        >
                          Cancel
                        </button>
                        <button 
                          onClick={savePersonalChanges} 
                          className="btn btn-primary btn-sm"
                          disabled={isLoading}
                        >
                          {isLoading ? (
                            <>
                              <span className="loading loading-spinner loading-xs"></span>
                              Saving...
                            </>
                          ) : 'Save'}
                        </button>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-4">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div className="bg-base-50 p-4 rounded-lg border border-base-200">
                        <h4 className="text-xs font-semibold uppercase tracking-wider text-base-content/60 mb-1">Full Name</h4>
                        <p className="text-base-content font-medium">{userData.name || 'Not provided'}</p>
                      </div>
                      
                      <div className="bg-base-50 p-4 rounded-lg border border-base-200">
                        <h4 className="text-xs font-semibold uppercase tracking-wider text-base-content/60 mb-1">Email</h4>
                        <p className="text-base-content font-medium truncate">{userData.email || 'Not provided'}</p>
                      </div>
                      
                      <div className="bg-base-50 p-4 rounded-lg border border-base-200">
                        <h4 className="text-xs font-semibold uppercase tracking-wider text-base-content/60 mb-1">Birth Year</h4>
                        <p className="text-base-content font-medium">{userData.birthYear || 'Not provided'}</p>
                      </div>
                      
                      <div className="bg-base-50 p-4 rounded-lg border border-base-200">
                        <h4 className="text-xs font-semibold uppercase tracking-wider text-base-content/60 mb-1">Gender</h4>
                        <p className="text-base-content font-medium capitalize">
                          {userData.gender ? userData.gender.replace('_', ' ') : 'Not provided'}
                        </p>
                      </div>
                    </div>
                    
                    <div className="divider my-4"></div>
                    
                    <div className="flex justify-center">
                      <button 
                        onClick={() => setShowPasswordModal(true)} 
                        className="btn btn-primary btn-sm"
                      >
                        Change Password
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
          
          {/* Sport Information Card */}
          <div className="card bg-base-100 shadow-md hover:shadow-lg transition-shadow duration-200 desktop-standard-card">
            <div className="card-body p-3 sm:p-4">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-xl font-bold text-primary whitespace-nowrap">Sport Information</h3>
                <button
                  onClick={editingSport ? cancelEditingSport : startEditingSport}
                  className="btn btn-ghost btn-sm btn-square hover:bg-base-200 transition-colors duration-200"
                  aria-label={editingSport ? 'Cancel editing' : 'Edit sport information'}
                >
                  {editingSport ? 'Cancel' : 'Edit'}
                </button>
              </div>
              
              <div className="min-h-[3rem]">
                {editingSport ? (
                  <div className="space-y-4">
                    <div className="form-control">
                      <label className="label">
                        <span className="label-text font-medium">Sport</span>
                      </label>
                      <select
                        name="sport"
                        value={editData.sport || ''}
                        onChange={handleSportChange}
                        className="select select-bordered w-full focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all duration-200"
                      >
                        <option value="">Select your sport</option>
                        {SPORTS_LIST.map((sport) => (
                          <option key={sport} value={sport}>{sport}</option>
                        ))}
                      </select>
                    </div>
                    
                    {editData.sport && editData.sport !== 'Other' && positionOptions.length > 0 && (
                      <div className="form-control">
                        <label className="label">
                          <span className="label-text font-medium">Position/Specialty</span>
                        </label>
                        <select
                          name="sportPosition"
                          value={editData.sportPosition || ''}
                          onChange={handlePositionChange}
                          className="select select-bordered w-full focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all duration-200"
                        >
                          <option value="">Select your position</option>
                          {positionOptions.map((position) => (
                            <option key={position} value={position}>{position}</option>
                          ))}
                        </select>
                      </div>
                    )}
                    
                    <div className="form-control">
                      <label className="label">
                        <span className="label-text font-medium">Years Playing</span>
                      </label>
                      <input
                        type="number"
                        name="yearsPlaying"
                        value={editData.yearsPlaying || 0}
                        onChange={handleEditChange}
                        className="input input-bordered w-full focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all duration-200"
                        placeholder="Years of experience"
                        min="0"
                        max="50"
                      />
                    </div>
                    
                    <div className="flex justify-end gap-2 mt-4">
                      <button 
                        onClick={cancelEditingSport} 
                        className="btn btn-ghost btn-sm"
                        disabled={isLoading}
                      >
                        Cancel
                      </button>
                      <button 
                        onClick={saveSportChanges} 
                        className="btn btn-primary btn-sm"
                        disabled={isLoading}
                      >
                        {isLoading ? (
                          <>
                            <span className="loading loading-spinner loading-xs"></span>
                            Saving...
                          </>
                        ) : 'Save'}
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-4">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div className="bg-base-50 p-4 rounded-lg border border-base-200">
                        <h4 className="text-xs font-semibold uppercase tracking-wider text-base-content/60 mb-1">Sport</h4>
                        <p className="text-base-content font-medium">{userData.sport || 'Not specified'}</p>
                      </div>
                      
                      <div className="bg-base-50 p-4 rounded-lg border border-base-200">
                        <h4 className="text-xs font-semibold uppercase tracking-wider text-base-content/60 mb-1">Position/Specialty</h4>
                        <p className="text-base-content font-medium">{userData.sportPosition || 'Not provided'}</p>
                      </div>
                      
                      <div className="bg-base-50 p-4 rounded-lg border border-base-200">
                        <h4 className="text-xs font-semibold uppercase tracking-wider text-base-content/60 mb-1">Years Playing</h4>
                        <p className="text-base-content font-medium">{userData.yearsPlaying || 'Not provided'}</p>
                      </div>
                      
                      <div className="bg-base-50 p-4 rounded-lg border border-base-200">
                        <h4 className="text-xs font-semibold uppercase tracking-wider text-base-content/60 mb-1">Age</h4>
                        <p className="text-base-content font-medium">{userData.age || calculateAge(userData.birthYear) || 'Not provided'}</p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
          
          {/* Team Information Section */}
          <div className="card bg-base-100 shadow-md hover:shadow-lg transition-shadow duration-200 desktop-standard-card" key={`team-section-${teamUpdateKey}`}>
            <div className="card-body p-3 sm:p-4">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-xl font-bold text-primary whitespace-nowrap">
                  Team Information {teamDetails ? `(${teamDetails.teamName})` : ''}
                </h3>
              </div>

               {isLoadingTeam ? (
                 <div className="flex justify-center py-4">
                   <span className="loading loading-spinner loading-md text-primary"></span>
                 </div>
               ) : teamError ? (
                 <div className="alert alert-error shadow-sm">
                   <AlertCircle className="w-5 h-5 flex-shrink-0" />
                   <span>{teamError}</span>
                 </div>
                ) : teamDetails ? (
                  <div className="space-y-4">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div className="bg-base-50 p-4 rounded-lg border border-base-200">
                        <h4 className="text-xs font-semibold uppercase tracking-wider text-base-content/60 mb-1">Team Name</h4>
                        <p className="text-base-content font-medium">{teamDetails.teamName}</p>
                      </div>
                      
                      <div className="bg-base-50 p-4 rounded-lg border border-base-200">
                        <h4 className="text-xs font-semibold uppercase tracking-wider text-base-content/60 mb-1">Sport</h4>
                        <p className="text-base-content font-medium">{teamDetails.teamSport}</p>
                      </div>
                      
                      <div className="bg-base-50 p-4 rounded-lg border border-base-200">
                        <h4 className="text-xs font-semibold uppercase tracking-wider text-base-content/60 mb-1">Age Group</h4>
                        <p className="text-base-content font-medium">{teamDetails.teamAgeGroup || 'Not specified'}</p>
                      </div>
                      
                      <div className="bg-base-50 p-4 rounded-lg border border-base-200">
                        <h4 className="text-xs font-semibold uppercase tracking-wider text-base-content/60 mb-1">Team Size</h4>
                        <p className="text-base-content font-medium">{teamDetails.teamSize || 'Not specified'}</p>
                      </div>
                      
                      <div className="bg-base-50 p-4 rounded-lg border border-base-200">
                        <h4 className="text-xs font-semibold uppercase tracking-wider text-base-content/60 mb-1">Your Role</h4>
                        <p className="text-base-content font-medium capitalize">{teamDetails.userRole}</p>
                      </div>
                      
                      <div className="bg-base-50 p-4 rounded-lg border border-base-200">
                        <h4 className="text-xs font-semibold uppercase tracking-wider text-base-content/60 mb-1">Coach Information</h4>
                        {teamDetails && teamDetails.coach ? (
                          <div>
                            <p className="text-base-content font-medium">{teamDetails.coach.name || 'Unknown Coach'}</p>
                            <p className="text-sm text-base-content/70">{teamDetails.coach.email || 'No email provided'}</p>
                          </div>
                        ) : (
                          <p className="text-base-content font-medium">No coach information available</p>
                        )}
                      </div>
                    </div>
                  </div>
               ) : (
                <div className="alert alert-info shadow-sm">
                  <InfoCircle className="w-5 h-5 flex-shrink-0" />
                  <span>No team information available.</span>
                </div>
              )}
            </div>
          </div>
          {/* Mental Performance Profile Card */}
          <div className="card bg-base-100 shadow-md hover:shadow-lg transition-shadow duration-200 desktop-standard-card">
            <div className="card-body p-3 sm:p-4">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-xl font-bold text-primary whitespace-nowrap">Mental Performance Profile</h3>
                <button 
                  onClick={() => setShowOnboardingModal(true)} 
                  className="btn btn-ghost btn-sm btn-square hover:bg-base-200 transition-colors duration-200"
                  disabled={isLoading}
                  aria-label="Update Mental Performance Profile"
                >
                  Edit
                </button>
              </div>
              
              <div className="space-y-4">
                <div className="bg-primary/5 p-4 rounded-lg border border-primary/20">
                  <div className="flex items-center gap-2 mb-3">
                    <div className="w-6 h-6 rounded-full bg-primary/20 flex items-center justify-center">
                      <span className="text-xs font-bold text-primary">1</span>
                    </div>
                    <h4 className="font-semibold text-primary">Why I Play My Sport</h4>

                  </div>
                  <p className="text-base-content whitespace-pre-line leading-relaxed text-sm">
                    {userData.whyIPlaySport || 'Not provided'}
                  </p>
                </div>
                
                <div className="bg-primary/5 p-4 rounded-lg border border-primary/20">
                  <div className="flex items-center gap-2 mb-3">
                    <div className="w-6 h-6 rounded-full bg-primary/20 flex items-center justify-center">
                      <span className="text-xs font-bold text-primary">2</span>
                    </div>
                    <h4 className="font-semibold text-primary">My Athletic Journey</h4>
                  </div>
                  <p className="text-base-content whitespace-pre-line leading-relaxed text-sm">
                    {userData.journey || 'Not provided'}
                  </p>
                </div>
                
                <div className="bg-primary/5 p-4 rounded-lg border border-primary/20">
                  <div className="flex items-center gap-2 mb-3">
                    <div className="w-6 h-6 rounded-full bg-primary/20 flex items-center justify-center">
                      <span className="text-xs font-bold text-primary">3</span>
                    </div>
                    <h4 className="font-semibold text-primary">My Goals</h4>
                  </div>
                  <p className="text-base-content whitespace-pre-line leading-relaxed text-sm">
                    {userData.goals || 'Not provided'}
                  </p>
                </div>
                
                <div className="bg-primary/5 p-4 rounded-lg border border-primary/20">
                  <div className="flex items-center gap-2 mb-3">
                    <div className="w-6 h-6 rounded-full bg-primary/20 flex items-center justify-center">
                      <span className="text-xs font-bold text-primary">4</span>
                    </div>
                    <h4 className="font-semibold text-primary">Expectations</h4>
                  </div>
                  <p className="text-base-content whitespace-pre-line leading-relaxed text-sm">
                    {userData.expectations || 'Not provided'}
                  </p>
                </div>
                
                <div className="bg-primary/5 p-4 rounded-lg border border-primary/20">
                  <div className="flex items-center gap-2 mb-3">
                    <div className="w-6 h-6 rounded-full bg-primary/20 flex items-center justify-center">
                      <span className="text-xs font-bold text-primary">5</span>
                    </div>
                    <h4 className="font-semibold text-primary">Current Mental State</h4>
                  </div>
                  <p className="text-base-content whitespace-pre-line leading-relaxed text-sm">
                    {userData.currentMentalState || 'Not provided'}
                  </p>
                </div>
                
                <div className="bg-primary/5 p-4 rounded-lg border border-primary/20">
                  <div className="flex items-center gap-2 mb-3">
                    <div className="w-6 h-6 rounded-full bg-primary/20 flex items-center justify-center">
                      <span className="text-xs font-bold text-primary">6</span>
                    </div>
                    <h4 className="font-semibold text-primary">Desired Mental State</h4>
                  </div>
                  <p className="text-base-content whitespace-pre-line leading-relaxed text-sm">
                    {userData.desiredMentalState || 'Not provided'}
                  </p>
                </div>
                
                {(['whyIPlaySport', 'journey', 'goals', 'expectations', 'currentMentalState', 'desiredMentalState'].some(field => userData[field] === 'Not provided')) && (
                  <div className="alert alert-error shadow-sm mb-4">
                    <AlertCircle className="w-5 h-5 flex-shrink-0" />
                    <span className="text-sm">Please complete your mental performance profile to unlock personalized insights.</span>
                  </div>
                )}
                
                <div className="text-center pt-2">
                  <button 
                    onClick={() => setShowOnboardingModal(true)} 
                    className="btn btn-primary btn-sm"
                  >
                    Update Profile
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Password Change Modal */}
      {showPasswordModal && (
        <Modal 
          isOpen={showPasswordModal}
          onClose={() => setShowPasswordModal(false)}
          title="Change Password"
        >
          <form onSubmit={handlePasswordSubmit} className="space-y-4">
            {passwordError && (
              <div className="alert alert-error shadow-sm">
                <AlertCircle className="w-5 h-5 flex-shrink-0" />
                <span className="text-sm">{passwordError}</span>
              </div>
            )}
            
            {passwordMessage && (
              <div className="alert alert-success shadow-sm">
                <CheckCircle className="w-5 h-5 flex-shrink-0" />
                <span className="text-sm">{passwordMessage}</span>
              </div>
            )}
            
            <div className="form-control">
              <label className="label">
                <span className="label-text font-medium">Current Password</span>
              </label>
              <input
                type="password"
                name="currentPassword"
                value={passwordData.currentPassword}
                onChange={handlePasswordChange}
                className="input input-bordered w-full focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all duration-200"
                placeholder="Enter your current password"
              />
            </div>
            
            <div className="form-control">
              <label className="label">
                <span className="label-text font-medium">New Password</span>
              </label>
              <input
                type="password"
                name="newPassword"
                value={passwordData.newPassword}
                onChange={handlePasswordChange}
                className="input input-bordered w-full focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all duration-200"
                placeholder="Enter your new password"
              />
            </div>
            
            <div className="form-control">
              <label className="label">
                <span className="label-text font-medium">Confirm New Password</span>
              </label>
              <input
                type="password"
                name="confirmPassword"
                value={passwordData.confirmPassword}
                onChange={handlePasswordChange}
                className="input input-bordered w-full focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all duration-200"
                placeholder="Confirm your new password"
              />
            </div>
            
            <div className="flex justify-end gap-2 mt-6">
              <button 
                type="button" 
                onClick={() => setShowPasswordModal(false)} 
                className="btn btn-ghost btn-sm"
                disabled={isLoading}
              >
                Cancel
              </button>
              <button 
                type="submit" 
                className="btn btn-primary btn-sm"
                disabled={isLoading}
              >
                {isLoading ? (
                  <>
                    <span className="loading loading-spinner loading-xs"></span>
                    Changing...
                  </>
                ) : 'Change Password'}
              </button>
            </div>
          </form>
        </Modal>
      )}
      
      {/* Onboarding Modal */}
      {showOnboardingModal && (
        <Onboarding 
          isOpen={showOnboardingModal}
          onClose={() => setShowOnboardingModal(false)}
          onComplete={handleOnboardingComplete}
          startStep={0}
        />
      )}
      
      {/* Debug Panel - Only visible when explicitly enabled with ?debug=profile query parameter */}
      {(window.location.search.includes('debug=profile') && (
        <ProfileDebugPanel userData={userData} />
      ))}
    </div>
  ); // This closes the main container div
}

// Main Profile component with error boundary
const Profile = () => {
  return (
    <ErrorBoundary>
      <ProfileContent />
    </ErrorBoundary>
  );
};

export default Profile;